import { Plus, Heart, Clock, Users, ShoppingBag, Eye } from "lucide-react";
import { fmtMoney } from "./plazaLib";
import { imgSrc } from "@/lib/dszImage";

// Threshold-gated social-proof pill. Only shown when counts are high
// enough to be believable — a "1 person bought this in 7 days" pill
// would hurt trust more than help.
function SocialProofPill({ proof }) {
  if (!proof) return null;
  const bought  = proof.bought  || 0;
  const added   = proof.added   || 0;
  const viewing = proof.viewing_recent || 0;
  // Pick the strongest signal available.
  if (bought >= 3) {
    return (
      <span className="hpz-card__proof hpz-card__proof--bought"
             data-testid="plaza-proof-bought">
        <ShoppingBag size={11} /> {bought} bought in 7 days
      </span>
    );
  }
  if (viewing >= 2) {
    return (
      <span className="hpz-card__proof hpz-card__proof--viewing"
             data-testid="plaza-proof-viewing">
        <Eye size={11} /> {viewing} viewing now
      </span>
    );
  }
  if (added >= 5) {
    return (
      <span className="hpz-card__proof hpz-card__proof--added"
             data-testid="plaza-proof-added">
        <Users size={11} /> In {added} carts this week
      </span>
    );
  }
  return null;
}

export default function SmartProductCard({ product, onOpen, onAdd,
                                            inWishlist, onToggleWishlist,
                                            proof }) {
  const firstImg = imgSrc(product, 0);
  const img = firstImg ||
              `https://placehold.co/400x400/fafaf9/9ca3af?text=${encodeURIComponent(product.brand || "HustlePlaza")}`;
  const price = product.selling_price_cents || product.rrp_cents || 0;
  const rrp = product.rrp_cents;
  const saving = rrp && rrp > price ? Math.round(((rrp - price) / rrp) * 100) : 0;
  const isPreorder = product.availability === "preorder";
  const restockDate = product.restock_eta_iso?.slice(0, 10);

  return (
    <article
      className="hpz-card"
      onClick={onOpen}
      data-testid={`plaza-product-${product.sku}`}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
    >
      <div className="hpz-card__img-wrap">
        <img src={img} alt={product.title} loading="lazy" />
        {isPreorder && (
          <span className="hpz-card__badge hpz-card__badge--preorder"
                 data-testid={`plaza-preorder-${product.sku}`}>
            <Clock size={11} /> Back in stock {restockDate}
          </span>
        )}
        {!isPreorder && product.is_new_arrival && (
          <span className="hpz-card__badge hpz-card__badge--new">NEW</span>
        )}
        {saving > 0 && (
          <span className="hpz-card__badge hpz-card__badge--save">
            -{saving}%
          </span>
        )}
        {(product.variant_count || 0) > 1 && (
          <span className="hpz-card__badge"
            data-testid={`plaza-card-options-${product.sku}`}
            style={{ background: "#1B3B2A", color: "#9FE2B8" }}>
            +{product.variant_count - 1} options
          </span>
        )}
        <button
          className={`hpz-card__heart ${inWishlist ? "active" : ""}`}
          onClick={(e) => { e.stopPropagation(); onToggleWishlist(); }}
          aria-label={inWishlist ? "Remove from wishlist" : "Save for later"}
          data-testid={`plaza-wishlist-${product.sku}`}
        >
          <Heart size={16} fill={inWishlist ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="hpz-card__body">
        {product.brand && product.brand !== "Does not apply" && (
          <p className="hpz-card__brand">{product.brand}</p>
        )}
        <h4 className="hpz-card__title">{product.title}</h4>
        <SocialProofPill proof={proof} />
        <div className="hpz-card__prices">
          {rrp && rrp > price && (
            <span className="hpz-card__rrp">${fmtMoney(rrp)}</span>
          )}
          <span className="hpz-card__price">${fmtMoney(price)}</span>
        </div>
        <button
          className="hpz-card__add"
          onClick={(e) => { e.stopPropagation(); onAdd(); }}
          data-testid={`plaza-add-${product.sku}`}
        >
          <Plus size={14} /> {isPreorder ? "Preorder" : "Add to cart"}
        </button>
      </div>
    </article>
  );
}
